import React from 'react';

/**
 * The quiet states a data panel can be in: loading, empty, or unavailable.
 *
 * One component for all three so a page full of panels fails in one visual
 * language rather than four. Every panel on Market Overview and Calendar uses
 * it, which is what keeps a page with ten dead endpoints looking deliberate
 * instead of broken.
 *
 * ### Why an error is not red
 *
 * `bear-red` means "down" everywhere else in this product — put walls, negative
 * GEX, losing positions. Spending it on a failed request teaches a reader to
 * scan past it, and a wall of red panels reads as an outage even when a single
 * feed is late. A missing panel is a quiet absence, not an alarm; the ones that
 * genuinely need attention are the trading colours.
 *
 * Errors therefore render in the same muted grey as "no data", distinguished by
 * wording rather than by colour. A reader who wants the cause hovers for the
 * title text.
 */

type Tone = 'loading' | 'empty' | 'unavailable';

const TEXT: Record<Tone, string> = {
  // Muted, all three. See the note above on why `unavailable` is not red.
  loading: 'text-gray-500',
  empty: 'text-gray-400',
  unavailable: 'text-gray-400',
};

export function PanelMessage({ tone, children, title }: {
  tone: Tone;
  children: React.ReactNode;
  /** Native tooltip — used to carry the underlying cause without showing it. */
  title?: string;
}): React.ReactElement {
  return (
    <p className={`text-sm py-2 ${TEXT[tone]}`} title={title}>
      {children}
    </p>
  );
}

/**
 * The standard "this endpoint is not built yet" state.
 *
 * ⚠️ Says the data is unavailable, not that something failed. Until QuantForge
 * serves these routes that is the literal truth, and it stays true afterwards
 * for the ordinary case of a feed being down — so this wording does not need
 * revisiting when the endpoints land.
 *
 * `what` names the missing thing so a reader can tell which panel is empty
 * without matching it to a heading: "Sector rotation unavailable".
 */
export function PanelUnavailable({ what }: { what: string }): React.ReactElement {
  return (
    <PanelMessage tone="unavailable" title="This data source is not connected yet.">
      {what} unavailable
    </PanelMessage>
  );
}

/** Shared loading state, so every panel says the same word. */
export function PanelLoading(): React.ReactElement {
  return <PanelMessage tone="loading">Loading&hellip;</PanelMessage>;
}
